import {LoadingView, Tag} from "@components";
import {Documents} from "@components/document";
import {CancelIcon, DeleteIcon, EditIcon, TaskIcon} from "@components/icons";
import {format} from "@constants";
import {taskApi} from "@core/api";
import {Task} from "@core/model";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import {CalendarIcon} from "@mui/x-date-pickers";
import {skipToken} from "@reduxjs/toolkit/query";
import {canManageOwned, translate} from "@util";
import {useSelector} from "react-redux";
import {Selectors} from "@core/store/selectors";
import dayjs from "dayjs";
import {useEffect, useState} from "react";
import {TaskInfo} from "./Info";

const CalendarDetail = ({
  task: taskProp,
  dialog,
  onClose,
  onEdit,
  onDelete,
}: {
  task: Task | undefined;
  dialog: boolean;
  onEdit: (t: Task) => void;
  onDelete: (t: Task) => void;
  onClose: () => void;
}) => {
  const [task, setTask] = useState<Task>();
  const user = useSelector(Selectors.user);

  const {data, isLoading, isFetching} = taskApi.useFindEntityById(
    taskProp?.id ?? skipToken,
  );

  useEffect(() => {
    if (data) {
      setTask({...data, recurring: data.recurrenceRule !== null});
    }
  }, [data]);

  useEffect(() => {
    setTask(taskProp);
  }, [taskProp]);

  // modifica e cancellazione come nel backend: admin, o project manager sui propri task
  const canManage = !!task && canManageOwned(user, task);

  const renderContent = (showTitle: boolean) => {
    return (
      <>
        <LoadingView loading={isLoading || isFetching} />
        <Stack gap={1.5} flex={1} minHeight={0}>
          <Box position={"absolute"} top={16} right={16}>
            <IconButton onClick={onClose}>
              <CancelIcon />
            </IconButton>
          </Box>
          {showTitle && (
            <Typography variant="h5" sx={{my: 1, mr: 5}} lineHeight={"24px"}>
              {task?.name}
            </Typography>
          )}

          <Stack
            gap={1.5}
            flex={1}
            minHeight={0}
            overflow={"auto"}
            sx={{"& > *": {flexShrink: 0}}}>
            <Typography
              variant="body2"
              gutterBottom
              sx={{maxHeight: 80, overflow: "auto"}}>
              {task?.description}
            </Typography>

            <Stack direction={"row"} gap={2} alignItems={"center"}>
              <CalendarIcon color="action" />
              <Stack>
                <Typography variant="caption" fontWeight={500} lineHeight={1.3}>
                  {translate("entity.task.start")}
                </Typography>
                <Typography variant="caption" lineHeight={1.3}>
                  {task?.start
                    ? dayjs(task.start).format(format.datetime.short)
                    : "-"}
                </Typography>
              </Stack>
              <Stack>
                <Typography variant="caption" fontWeight={500} lineHeight={1.3}>
                  {translate("entity.task.end")}
                </Typography>
                <Typography variant="caption" lineHeight={1.3}>
                  {task?.start
                    ? dayjs(task.end).format(format.datetime.short)
                    : "-"}
                </Typography>
              </Stack>
            </Stack>

            <Stack direction={"row"} gap={1} flexWrap={"wrap"}>
              {task?.tags?.map(tag => (
                <Tag key={tag} tag={tag} container="chip" />
              ))}
            </Stack>

            <Documents
              type="task"
              entity={task}
              sx={{bgcolor: "background.paper", borderRadius: 2}}
            />

            <TaskInfo task={task!} />
          </Stack>
        </Stack>
      </>
    );
  };

  if (task && dialog) {
    return (
      <Dialog open={Boolean(task && dialog)} onClose={onClose} fullScreen>
        <Stack direction="row" mt={2} justifyContent="center">
          <Box
            sx={{
              bgcolor: "secondary.dark",
              width: "64px",
              borderRadius: "32px",
              height: "64px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}>
            <TaskIcon fontSize="large" />
          </Box>
        </Stack>
        <DialogTitle align="center">{task?.name}</DialogTitle>
        <DialogContent>{renderContent(false)}</DialogContent>
        {canManage && (
          <DialogActions>
            <Button
              onClick={() => onDelete(task!)}
              color="error"
              endIcon={<DeleteIcon />}
              variant="contained">
              {translate("common.action.delete")}
            </Button>
            <Button
              onClick={() => onEdit(task!)}
              color="primary"
              endIcon={<EditIcon />}
              variant="contained">
              {translate("common.action.edit")}
            </Button>
          </DialogActions>
        )}
      </Dialog>
    );
  }

  if (task) {
    return (
      <Paper
        elevation={0}
        sx={{
          width: 400,
          ml: 2,
          p: 2,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          position: "relative",
          backgroundImage: "var(--Paper-overlay)",
        }}>
        {renderContent(true)}
        {canManage && (
          <Stack direction="row" mt={1} justifyContent="flex-end" gap={2}>
            <Button
              onClick={() => onDelete(task!)}
              color="error"
              endIcon={<DeleteIcon />}
              variant="contained">
              {translate("common.action.delete")}
            </Button>
            <Button
              onClick={() => onEdit(task!)}
              color="primary"
              endIcon={<EditIcon />}
              variant="contained">
              {translate("common.action.edit")}
            </Button>
          </Stack>
        )}
      </Paper>
    );
  }

  return <></>;
};

export {CalendarDetail};
